import client from '@/graphql/client'
import CardTypes from '@/graphql/queries/CardTypes'
import CreateCardType from '@/graphql/mutations/CreateCardType'
import UpdateCardType from '@/graphql/mutations/UpdateCardType'
import DeleteCardType from '@/graphql/mutations/DeleteCardType'
import OnCreateCardType from '@/graphql/subscriptions/OnCreateCardType'
import OnUpdateCardType from '@/graphql/subscriptions/OnUpdateCardType'
import OnDeleteCardType from '@/graphql/subscriptions/OnDeleteCardType'
import { addToSubscriptions } from '@/api/subscriptionHelpers'

export const cardTypes = {
  namespaced: true,
  state: {
    cardTypes: null,
    subscriptions: null
  },
  mutations: {
    setCardTypes (state, payload) {
      state.cardTypes = payload
    },
    setSubscriptions (state, payload) {
      state.subscriptions = payload
    },
    unsubscribeFromAll (state) {
      if (state.subscriptions) {
        for (const subscription of state.subscriptions) {
          subscription.unsubscribe()
        }
      }
      state.subscriptions = null
    },
    addCardType (state, payload) {
      const index = state.cardTypes.findIndex((cardType) => cardType.id === payload.id)
      if (index === -1) {
        state.cardTypes.push(payload)
      }
    },
    updateCardType (state, payload) {
      // This update method is used to preserve order of appearance for the card types, because sometimes,
      // server is returning a payload with card types in a different order, making tests flaky
      payload.cardTypes.forEach((newType) => {
        const index = state.cardTypes.findIndex((type) => type.id === newType.id)
        if (index !== -1) {
          state.cardTypes[index] = newType
        }
      })
    },
    deleteCardType (state, payload) {
      const index = state.cardTypes.findIndex((cardType) => cardType.id === payload.id)
      if (index >= 0) {
        state.cardTypes.splice(index, 1)
      }
    },
    sortCardTypes (state) {
      state.cardTypes.sort((a, b) => {
        return a.order < b.order
          ? -1
          : a.order > b.order
            ? 1
            : 0
      })
    }
  },
  actions: {
    async createCardType ({ commit, dispatch, getters }, { workflowId, input }) {
      try {
        const res = await client.query({
          operationName: 'createCardType',
          query: CreateCardType,
          variables: {
            workflowId,
            input
          }
        })
        commit('addCardType', res.createCardType)
        commit('sortCardTypes')
        commit('snackbar/showMessage', { content: 'Updated' }, { root: true })
        return res.createCardType
      } catch (error) {
        commit('snackbar/showMessage', { content: 'Error saving, try again!', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
      }
    },

    async updateCardType ({ commit, dispatch, getters }, { id, workflowId, input }) {
      try {
        const res = await client.query({
          operationName: 'updateCardType',
          query: UpdateCardType,
          variables: {
            workflowId,
            id,
            input
          }
        })
        commit('updateCardType', res.updateCardType)
        commit('sortCardTypes')
        commit('snackbar/showMessage', { content: 'Updated' }, { root: true })
      } catch (error) {
        commit('snackbar/showMessage', { content: 'Error saving, try again!', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
      }
    },

    async deleteCardType ({ commit, dispatch, getters }, { id, workflowId }) {
      try {
        const res = await client.query({
          operationName: 'deleteCardType',
          query: DeleteCardType,
          variables: {
            workflowId,
            id
          }
        })
        commit('deleteCardType', res.deleteCardType)
        commit('snackbar/showMessage', { content: 'Deleted' }, { root: true })
      } catch (error) {
        commit('snackbar/showMessage', { content: 'Error saving, try again!', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
      }
    },

    async listCardTypes ({ commit, rootState }, { workflowId }) {
      try {
        const res = await client.query({
          operationName: 'cardTypes',
          query: CardTypes,
          variables: { workflowId }
        })
        commit('setCardTypes', res.cardTypes)
        commit('sortCardTypes')

        // Unsubscribe from previous subscriptions before creating new ones
        commit('unsubscribeFromAll')

        if (rootState.isUserLoggedIn === false || rootState.workflow.isViewOnlyEnabled === true) {
          return
        }

        const subscriptions = []
        const addSubscription = (query, variables, callback) => {
          const sub = addToSubscriptions(query, variables, callback)
          subscriptions.push(sub)
        }

        addSubscription(OnCreateCardType, { workflowId }, (data) => {
          commit('addCardType', data.onCreateCardType)
          commit('sortCardTypes')
        })

        addSubscription(OnUpdateCardType, { workflowId }, (data) => {
          commit('updateCardType', data.onUpdateCardType)
          commit('sortCardTypes')
        })

        addSubscription(OnDeleteCardType, { workflowId }, (data) => {
          commit('deleteCardType', data.onDeleteCardType)
        })

        commit('setSubscriptions', subscriptions)
      } catch (error) {
        console.error('Failed to load card types:', error)
        commit('setCardTypes', [])
        commit('unsubscribeFromAll')
      }
    }
  },
  getters: {
    defaultCardType: (state) => {
      return state.cardTypes.find((cardType) => cardType.defaultType)
    }
  }
}
